/**
 * Deterministic seeded RNG. THE ONLY PLACE Math.random MAY BE REFERENCED IS NOWHERE —
 * this file does not use it either. See ARCHITECTURE.md §6.
 *
 * sfc32 — small, fast, passes PractRand, and identical across every JS engine because it
 * uses only uint32 ops.
 */

/** FNV-1a style string hash → uint32. Stable across engines. */
export function hashString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Scramble a uint32 seed into four well-distributed uint32 lanes. */
function seedLanes(seed) {
  let s = seed >>> 0;
  const lane = () => {
    // splitmix32
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
  return [lane(), lane(), lane(), lane()];
}

export class Rng {
  constructor(seed = 1) {
    this.seed = typeof seed === 'string' ? hashString(seed) : seed >>> 0;
    const [a, b, c, d] = seedLanes(this.seed);
    this._a = a; this._b = b; this._c = c; this._d = d;
    this._count = 0;
  }

  /** uint32 in [0, 2^32) */
  u32() {
    let a = this._a, b = this._b, c = this._c, d = this._d;
    const t = (a + b | 0) + d | 0;
    d = d + 1 | 0;
    a = b ^ (b >>> 9);
    b = c + (c << 3) | 0;
    c = (c << 21) | (c >>> 11);
    c = c + t | 0;
    this._a = a; this._b = b; this._c = c; this._d = d;
    this._count++;
    return t >>> 0;
  }

  /** float in [0,1) */
  next() { return this.u32() / 4294967296; }

  /** float in [min,max) */
  range(min, max) { return min + this.next() * (max - min); }

  /** integer in [min,maxExcl) */
  int(min, maxExcl) { return min + Math.floor(this.next() * (maxExcl - min)); }

  /** true with probability p */
  chance(p) { return this.next() < p; }

  /** ±amount */
  spread(amount) { return (this.next() * 2 - 1) * amount; }

  pick(arr) { return arr[Math.floor(this.next() * arr.length)]; }

  /** Weighted pick. weights need not be normalised. */
  pickWeighted(arr, weights) {
    let total = 0;
    for (let i = 0; i < weights.length; i++) total += weights[i];
    let r = this.next() * total;
    for (let i = 0; i < arr.length; i++) {
      r -= weights[i];
      if (r <= 0) return arr[i];
    }
    return arr[arr.length - 1];
  }

  /** In-place Fisher-Yates. Deterministic. */
  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(0, i + 1);
      const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  /** Approximately normal via sum of 3 uniforms. Cheap, deterministic, good enough. */
  gaussian(mean = 0, sd = 1) {
    const u = (this.next() + this.next() + this.next()) / 3;
    return mean + (u - 0.5) * 3.4641016 * sd;
  }

  /** Unit-ish 2D direction. */
  dir2(out = { x: 0, y: 0 }) {
    const a = this.next() * Math.PI * 2;
    out.x = Math.cos(a); out.y = Math.sin(a);
    return out;
  }

  /**
   * Derive an independent stream. ALWAYS fork per-subsystem in init() so one system's
   * draw count cannot perturb another's sequence. See ARCHITECTURE.md §6.
   */
  fork(label) {
    return new Rng((this.seed ^ hashString(String(label))) >>> 0);
  }

  /** Snapshot / restore for harness reproducibility checks. */
  getState() { return [this._a, this._b, this._c, this._d, this._count]; }
  setState(s) { [this._a, this._b, this._c, this._d, this._count] = s; }
}

/**
 * Deterministic value noise — for procedural textures and terrain-ish variation where you
 * want spatial coherence rather than a stream. Seeded, no allocation.
 */
export class ValueNoise2D {
  constructor(seed = 1) { this.seed = (typeof seed === 'string' ? hashString(seed) : seed) >>> 0; }

  _h(ix, iy) {
    let h = this.seed;
    h = Math.imul(h ^ (ix | 0), 0x27d4eb2d) >>> 0;
    h = Math.imul(h ^ (iy | 0), 0x165667b1) >>> 0;
    h = (h ^ (h >>> 15)) >>> 0;
    return h / 4294967296;
  }

  /** Smooth value noise in [0,1]. */
  sample(x, y) {
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const fx = x - x0, fy = y - y0;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const a = this._h(x0, y0), b = this._h(x0 + 1, y0);
    const c = this._h(x0, y0 + 1), d = this._h(x0 + 1, y0 + 1);
    const top = a + (b - a) * sx;
    const bot = c + (d - c) * sx;
    return top + (bot - top) * sy;
  }

  /** Fractal sum. octaves ~4, persistence ~0.5. Returns [0,1]. */
  fbm(x, y, octaves = 4, persistence = 0.5, lacunarity = 2) {
    let sum = 0, amp = 1, freq = 1, norm = 0;
    for (let i = 0; i < octaves; i++) {
      sum += this.sample(x * freq, y * freq) * amp;
      norm += amp;
      amp *= persistence;
      freq *= lacunarity;
    }
    return sum / norm;
  }

  /** Ridged variant — good for cracks and asphalt fissures. */
  ridged(x, y, octaves = 4) {
    let sum = 0, amp = 1, freq = 1, norm = 0;
    for (let i = 0; i < octaves; i++) {
      const n = Math.abs(this.sample(x * freq, y * freq) * 2 - 1);
      sum += (1 - n) * amp;
      norm += amp;
      amp *= 0.5; freq *= 2;
    }
    return sum / norm;
  }
}
